/**
 * API base URL. Empty string means requests use relative paths and go through
 * the same origin. In local development Vite proxies /api to the backend.
 */
export const API_BASE = "";

/**
 * Canonical public site URL used for share links and redirects.
 * Should match the custom domain configured in Cloudflare Pages.
 */
// 站點 URL：模板以 runtime origin 為準（部署在任何客戶域名即自動正確）
export const SITE_URL = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "https://example.com";

function isJsonString(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Unified API fetch wrapper.
 * - Prefixes paths with /api if not already prefixed
 * - Adds JSON content-type for object bodies
 * - Adds Authorization header for admin routes when admin_token present
 * - Adds Authorization header for member routes when customer token present
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const normalizedPath = path.startsWith('/api') ? path : `/api${path}`;
  const url = `${API_BASE}${normalizedPath}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  // Auto set Content-Type for JSON bodies
  if (
    options.body &&
    typeof options.body === 'string' &&
    !headers['Content-Type'] &&
    !headers['content-type'] &&
    isJsonString(options.body)
  ) {
    headers['Content-Type'] = 'application/json';
  }

  // Admin auth token for admin routes only
  const adminToken = localStorage.getItem('admin_token');
  if (adminToken && (normalizedPath.startsWith('/api/admin') || normalizedPath.startsWith('/api/public/admin'))) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }

  // Customer auth token for member routes only
  const customerToken = localStorage.getItem('x1493_auth_token');
  if (customerToken && normalizedPath.startsWith('/api/public/members')) {
    headers['Authorization'] = `Bearer ${customerToken}`;
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Helper to parse JSON response with basic error handling.
 */
export async function parseJson<T = unknown>(response: Response): Promise<T> {
  const text = await response.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  detail?: string;
}

export async function apiFetchJson<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const res = await apiFetch(path, options);
  const json = await parseJson<ApiResponse<T>>(res);
  if (!res.ok && !json.error) {
    return { success: false, error: `HTTP ${res.status}` };
  }
  return json;
}
